import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Loader2, MessagesSquare, Sparkles, Check, X, Trash2, Plus, ShieldCheck, RotateCcw, Repeat,
  ThumbsUp, ThumbsDown, MessageSquareWarning,
} from "lucide-react";

const NAVY = "#1C4358", GREEN = "#2F7D5A", RED = "#9C1E22", AMBER = "#B7791F";
const STATUS_HEX = { pending: AMBER, approved: GREEN, rejected: RED };
const pretty = (s) => String(s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function ChatQA() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("pending");
  const [drafts, setDrafts] = useState({});     // id -> answer text
  const [busy, setBusy] = useState({});          // id -> bool
  const [newOpen, setNewOpen] = useState(false);
  const [nu, setNu] = useState({ question: "", suggested_answer: "" });
  const [fb, setFb] = useState(null);

  const loadFeedback = useCallback(async () => {
    try { const { data } = await api.get("/qa/feedback"); setFb(data); } catch { /* admin only */ }
  }, []);
  useEffect(() => { loadFeedback(); }, [loadFeedback]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/qa/items", { params: { status: tab } });
      setData(data);
      const d = {}; (data.items || []).forEach((it) => { d[it.id] = it.suggested_answer || ""; });
      setDrafts(d);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load Q&A queue (admin only)."); }
    finally { setLoading(false); }
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  const setB = (id, v) => setBusy((p) => ({ ...p, [id]: v }));
  const suggest = async (id) => {
    setB(id, true);
    try { const { data } = await api.post(`/qa/items/${id}/suggest`); setDrafts((p) => ({ ...p, [id]: data.suggested_answer })); toast.success("AI drafted a safe answer — review before approving."); }
    catch (e) { toast.error(e?.response?.data?.detail || "Suggestion failed."); }
    finally { setB(id, false); }
  };
  const saveDraft = async (id) => { try { await api.put(`/qa/items/${id}`, { suggested_answer: drafts[id] || "" }); toast.success("Saved."); } catch { toast.error("Save failed."); } };
  const approve = async (id) => {
    setB(id, true);
    try { await api.put(`/qa/items/${id}`, { suggested_answer: drafts[id] || "" }); await api.post(`/qa/items/${id}/approve`); toast.success("Approved & published to knowledge base."); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Approve failed. Add an answer first."); }
    finally { setB(id, false); }
  };
  const reject = async (id) => { setB(id, true); try { await api.post(`/qa/items/${id}/reject`); toast.success("Rejected."); load(); } catch { toast.error("Failed."); } finally { setB(id, false); } };
  const markReviewed = async (id) => { try { await api.post(`/qa/items/${id}/feedback-reviewed`); toast.success("Feedback marked reviewed."); load(); loadFeedback(); } catch { toast.error("Failed."); } };
  const del = async (id) => { try { await api.delete(`/qa/items/${id}`); toast.success("Deleted."); load(); } catch { toast.error("Failed."); } };
  const createManual = async () => {
    if (!nu.question.trim()) { toast.error("Question is required."); return; }
    try { await api.post("/qa/items", nu); toast.success("Added to review queue."); setNewOpen(false); setNu({ question: "", suggested_answer: "" }); setTab("pending"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed."); }
  };

  const counts = data?.counts || {};

  return (
    <div className="pb-16 max-w-4xl" data-testid="chat-qa">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-center gap-2"><MessagesSquare size={22} className="text-[#1C4358]" /><h1 className="text-2xl font-display font-bold text-[#1C4358]">Q&amp;A Review Queue</h1></div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} className="border-[#D6E0E7] text-[#1C4358] gap-1"><RotateCcw size={14} /> Refresh</Button>
          <Button size="sm" data-testid="qa-new-btn" onClick={() => setNewOpen((v) => !v)} className="bg-[#9C1E22] hover:bg-[#861A1D] text-white gap-1"><Plus size={15} /> Add question</Button>
        </div>
      </div>

      <div className="flex items-start gap-2 rounded-lg px-3 py-2 text-[12px] mb-3" style={{ backgroundColor: "rgba(44,100,128,0.08)", color: "#22536B" }}>
        <ShieldCheck size={14} className="mt-0.5 shrink-0" /> AI can suggest answers, but nothing is published until you approve it. Personal details are stripped automatically, and legal / medical / financial / care-placement / real-estate safeguards always apply.
      </div>

      {fb && (
        <Card className="p-4 border-[#E6EDF2] mb-3" data-testid="qa-feedback-summary">
          <div className="flex items-center gap-2 mb-2"><MessageSquareWarning size={16} className="text-[#B7791F]" /><span className="text-[14px] font-semibold text-[#1C4358]">Family feedback on published answers</span></div>
          <div className="flex flex-wrap gap-4 text-[13px]">
            <span className="inline-flex items-center gap-1 text-[#2F7D5A]" data-testid="qa-fb-yes"><ThumbsUp size={14} /> {fb.helpful_yes} helpful</span>
            <span className="inline-flex items-center gap-1 text-[#9C1E22]" data-testid="qa-fb-no"><ThumbsDown size={14} /> {fb.helpful_no} not helpful</span>
            <span className="inline-flex items-center gap-1 text-[#B7791F]" data-testid="qa-fb-needsreview">{fb.needs_review} answer{fb.needs_review === 1 ? "" : "s"} need review</span>
          </div>
          {(fb.recent || []).length > 0 && (
            <div className="mt-3 space-y-1.5" data-testid="qa-fb-recent">
              <div className="text-[11px] uppercase tracking-wide text-[#9AA5B1]">Recent comments</div>
              {fb.recent.slice(0, 6).map((r) => (
                <div key={r.id} className="text-[12px] text-[#4B5563] border-l-2 border-[#E6EDF2] pl-2">
                  <span className={r.helpful ? "text-[#2F7D5A]" : "text-[#9C1E22]"}>{r.helpful ? "👍" : "👎"}</span>{" "}
                  <span className="font-medium text-[#1C4358]">{r.question}</span>
                  {r.comment && <> — “{r.comment}”</>}
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {newOpen && (
        <Card className="p-4 border-[#E6EDF2] mb-3" data-testid="qa-new-card">
          <label className="text-[11px] text-[#4B5563]">Question</label>
          <Input data-testid="qa-new-question" value={nu.question} onChange={(e) => setNu({ ...nu, question: e.target.value })} className="h-9 text-[14px] mb-2" />
          <label className="text-[11px] text-[#4B5563]">Answer (optional)</label>
          <Textarea value={nu.suggested_answer} onChange={(e) => setNu({ ...nu, suggested_answer: e.target.value })} rows={2} className="text-[14px]" />
          <div className="flex gap-2 mt-2"><Button size="sm" data-testid="qa-new-submit" onClick={createManual} className="bg-[#1C4358] text-white">Add</Button><Button size="sm" variant="outline" onClick={() => setNewOpen(false)} className="border-[#D6E0E7] text-[#1C4358]">Cancel</Button></div>
        </Card>
      )}

      <div className="flex gap-1.5 mb-4">
        {["pending", "approved", "rejected"].map((s) => (
          <button key={s} data-testid={`qa-tab-${s}`} onClick={() => setTab(s)}
            className="text-[13px] font-semibold px-3 py-1.5 rounded-full border transition-colors"
            style={{ borderColor: tab === s ? STATUS_HEX[s] : "#D6E0E7", color: tab === s ? STATUS_HEX[s] : "#4B5563", backgroundColor: tab === s ? `${STATUS_HEX[s]}14` : "white" }}>
            {pretty(s)} <span className="tabular-nums">{counts[s] ?? 0}</span>
          </button>
        ))}
      </div>

      {loading && !data ? <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#1C4358]" size={28} /></div> :
        (data.items || []).length === 0 ? <div className="text-center py-14 text-[#4B5563]"><MessagesSquare className="mx-auto mb-2 text-[#9AA5B1]" /> Nothing in “{pretty(tab)}”.</div> : (
          <div className="space-y-3">
            {data.items.map((it) => (
              <Card key={it.id} data-testid={`qa-item-${it.id}`} className="p-4 border-[#E6EDF2]">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-[#1C4358] text-[15px]">{it.question}</div>
                    <div className="text-[11px] text-[#4B5563] mt-0.5 flex items-center gap-2 flex-wrap">
                      <span>Source: {pretty(it.source)}</span>
                      {it.occurrences > 1 && <span className="flex items-center gap-0.5 text-[#B7791F]"><Repeat size={11} /> asked {it.occurrences}×</span>}
                      {it.service_lane && <span>· {pretty(it.service_lane)}</span>}
                      {it.ai_suggested && <span className="text-[#7C3AED]">· AI-drafted</span>}
                      {(it.helpful_yes || it.helpful_no) ? <span className="inline-flex items-center gap-1">· <ThumbsUp size={11} className="text-[#2F7D5A]" /> {it.helpful_yes || 0} <ThumbsDown size={11} className="text-[#9C1E22]" /> {it.helpful_no || 0}</span> : null}
                      {it.needs_feedback_review && <span className="inline-flex items-center gap-0.5 text-[#B7791F] font-semibold" data-testid={`qa-needsreview-${it.id}`}><MessageSquareWarning size={11} /> feedback needs review</span>}
                    </div>
                  </div>
                  <button onClick={() => del(it.id)} data-testid={`qa-delete-${it.id}`} className="text-[#9C1E22] hover:bg-[#9C1E22]/10 rounded p-1"><Trash2 size={15} /></button>
                </div>
                <Textarea data-testid={`qa-answer-${it.id}`} value={drafts[it.id] ?? ""} onChange={(e) => setDrafts((p) => ({ ...p, [it.id]: e.target.value }))} rows={3} placeholder="Approved answer (safe wording only)…" className="text-[14px] mt-3" />
                <div className="flex flex-wrap gap-2 mt-2">
                  <Button size="sm" variant="outline" data-testid={`qa-suggest-${it.id}`} onClick={() => suggest(it.id)} disabled={busy[it.id]} className="border-[#7C3AED]/40 text-[#7C3AED] gap-1">{busy[it.id] ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} AI suggest</Button>
                  <Button size="sm" variant="outline" onClick={() => saveDraft(it.id)} className="border-[#D6E0E7] text-[#1C4358]">Save draft</Button>
                  {tab !== "approved" && <Button size="sm" data-testid={`qa-approve-${it.id}`} onClick={() => approve(it.id)} disabled={busy[it.id]} className="bg-[#2F7D5A] hover:bg-[#276848] text-white gap-1"><Check size={14} /> Approve &amp; publish</Button>}
                  {tab !== "rejected" && <Button size="sm" variant="outline" data-testid={`qa-reject-${it.id}`} onClick={() => reject(it.id)} className="border-[#9C1E22]/40 text-[#9C1E22] gap-1"><X size={14} /> Reject</Button>}
                  {it.needs_feedback_review && <Button size="sm" variant="outline" data-testid={`qa-mark-reviewed-${it.id}`} onClick={() => markReviewed(it.id)} className="border-[#B7791F]/40 text-[#B7791F] gap-1"><Check size={14} /> Mark reviewed</Button>}
                </div>
              </Card>
            ))}
          </div>
        )}
    </div>
  );
}
